import { useCallback, useEffect, useRef } from "react";
import type { User } from "@tabipla/shared/domain";
import type { Participant, UseAuthCommonOptions, UseAuthResult } from "./types";

// Global flag to prevent multiple concurrent auth checks
let isCheckingAuth = false;

/**
 * Participant から User 形式に変換
 */
function participantToUser(participant: Participant): User {
  return {
    id: participant.id,
    email: participant.email ?? "",
    name: participant.name,
    emailVerified: participant.emailVerified ?? false,
    image: participant.image ?? null,
    avatarEmoji: participant.avatarEmoji ?? null,
    avatarColor: participant.avatarColor ?? null,
    lineUserId: participant.lineUserId ?? null,
    createdAt: participant.createdAt ?? new Date(),
    updatedAt: participant.updatedAt ?? new Date(),
  };
}

/**
 * 認証のコア機能を提供する統一フック
 *
 * - guestSupport なし: Mekke のようなユーザー認証のみのアプリ向け
 * - guestSupport あり: Tabitto/Waritto のようなゲストユーザーをサポートするアプリ向け
 *
 * @example
 * // ゲスト非対応（Mekke）
 * const authActions = useAuthCommon({
 *   apiClient,
 *   authClient: { signIn, signUp, signOut },
 *   setUser,
 *   setIsLoading,
 *   setIsAuthChecked,
 *   navigate,
 * });
 *
 * @example
 * // ゲスト対応（Tabitto/Waritto）
 * const authActions = useAuthCommon({
 *   apiClient,
 *   authClient: { signIn, signUp, signOut },
 *   setUser,
 *   setIsLoading,
 *   setIsAuthChecked,
 *   navigate,
 *   guestSupport: { setParticipant },
 * });
 */
export function useAuthCommon(
  options: UseAuthCommonOptions
): Pick<UseAuthResult, "login" | "register" | "logout" | "loginWithLine"> {
  const {
    apiClient,
    authClient,
    setUser,
    setIsLoading,
    setIsAuthChecked,
    navigate,
    loginRedirectPath = "/",
    logoutRedirectPath = "/login",
    guestSupport,
  } = options;

  const hasCheckedRef = useRef(false);

  // Check authentication status on mount (only once)
  useEffect(() => {
    // Skip if already checked or currently checking
    if (hasCheckedRef.current || isCheckingAuth) {
      return;
    }

    hasCheckedRef.current = true;
    isCheckingAuth = true;

    const checkAuth = async () => {
      // API で認証状態を確認
      // スプラッシュスクリーンがローディング中を表示するため、
      // 常に API を呼び出してOK
      try {
        const response = await apiClient.get<Participant | null>("/api/me");
        if (response.success && response.data) {
          if (guestSupport) {
            // ゲスト対応モード
            guestSupport.setParticipant(response.data);
            if (response.data.type === "user") {
              setUser(participantToUser(response.data));
            } else {
              setUser(null);
            }
          } else {
            // ユーザー認証のみモード
            setUser(response.data as unknown as User);
          }
        } else {
          if (guestSupport) {
            guestSupport.setParticipant(null);
          }
          setUser(null);
        }
      } catch {
        if (guestSupport) {
          guestSupport.setParticipant(null);
        }
        setUser(null);
      } finally {
        setIsLoading(false);
        setIsAuthChecked(true);
        isCheckingAuth = false;
      }
    };

    checkAuth();
  }, [apiClient, setUser, setIsLoading, setIsAuthChecked, guestSupport]);

  const login = useCallback(
    async (email: string, password: string) => {
      const result = await authClient.signIn.email({ email, password });
      if (result.error) {
        throw new Error(result.error.message ?? "Login failed");
      }
      // Re-check auth after login
      const response = await apiClient.get<Participant | null>("/api/me");
      if (response.success && response.data) {
        if (guestSupport) {
          guestSupport.setParticipant(response.data);
          if (response.data.type === "user") {
            setUser(participantToUser(response.data));
          }
        } else {
          setUser(response.data as unknown as User);
        }
      }
      navigate(loginRedirectPath);
    },
    [apiClient, authClient, setUser, navigate, loginRedirectPath, guestSupport]
  );

  const register = useCallback(
    async (email: string, password: string, name: string) => {
      const result = await authClient.signUp.email({ email, password, name });
      if (result.error) {
        throw new Error(result.error.message ?? "Registration failed");
      }
      // Re-check auth after registration
      const response = await apiClient.get<Participant | null>("/api/me");
      if (response.success && response.data) {
        if (guestSupport) {
          guestSupport.setParticipant(response.data);
          if (response.data.type === "user") {
            setUser(participantToUser(response.data));
          }
        } else {
          setUser(response.data as unknown as User);
        }
      }
      navigate(loginRedirectPath);
    },
    [apiClient, authClient, setUser, navigate, loginRedirectPath, guestSupport]
  );

  const logout = useCallback(async () => {
    if (guestSupport) {
      // ゲストトークンも削除
      localStorage.removeItem("guest_token");
      guestSupport.setParticipant(null);
    }
    // Sign out from Better Auth
    await authClient.signOut();
    setUser(null);
    navigate(logoutRedirectPath);
  }, [authClient, setUser, navigate, logoutRedirectPath, guestSupport]);

  const loginWithLine = useCallback(async () => {
    if (!authClient.signIn.social) {
      throw new Error("Social login not supported");
    }
    // This will redirect to LINE OAuth page
    // callbackURL でクロスドメインリダイレクト（API → フロントエンド）
    await authClient.signIn.social({
      provider: "line",
      callbackURL: window.location.origin + loginRedirectPath,
    } as { provider: string; callbackURL?: string });
  }, [authClient, loginRedirectPath]);

  return {
    login,
    register,
    logout,
    loginWithLine,
  };
}
